import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { authAPI } from '../api';
import MaestraLogo from '../components/MaestraLogo';
import RateLimitNotice from '../components/RateLimitNotice';
import PlatformFooter from '../components/PlatformFooter';

const ResetPassword = () => {
  const navigate = useNavigate();
  const { token } = useParams();
  document.title = 'Maestra - Set New Password';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [rateLimitMessage, setRateLimitMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setRateLimitMessage('');

    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      await authAPI.resetPassword(token, password);
      setSuccess(true);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || 'This reset link is invalid or has expired.';
      if (status === 429) {
        setRateLimitMessage(detail);
      } else {
        setError(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <div className="login-container">
          <div style={{ marginBottom: '32px' }}><MaestraLogo size="sm" /></div>

          {!success ? (
            <>
              <h1 className="login-title" data-testid="reset-password-title">Set a new password</h1>
              <p style={{
                fontFamily: 'Cormorant Garamond, Playfair Display, serif',
                fontStyle: 'italic',
                fontSize: '14px',
                color: 'var(--kahla-text-light)',
                textAlign: 'center',
                marginBottom: '24px',
                lineHeight: '1.7',
              }}>
                Choose a new password. You'll sign in with your email and this password from now on.
              </p>

              {error && <div className="login-error" data-testid="reset-password-error">{error}</div>}
              <RateLimitNotice message={rateLimitMessage} testId="reset-password-rate-limit-notice" />

              <form className="login-form" onSubmit={handleSubmit}>
                <div className="login-field">
                  <label>New Password</label>
                  <input
                    data-testid="reset-password-input"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 6 characters"
                    required
                    minLength={6}
                  />
                </div>

                <div className="login-field">
                  <label>Confirm Password</label>
                  <input
                    data-testid="reset-password-confirm-input"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Repeat your password"
                    required
                    minLength={6}
                  />
                </div>

                <button
                  data-testid="reset-password-submit-btn"
                  type="submit"
                  className="btn-primary"
                  disabled={loading}
                  style={{ width: '100%', marginTop: '12px' }}
                >
                  {loading ? 'Updating...' : 'Update password'}
                </button>
              </form>
            </>
          ) : (
            <div data-testid="reset-password-success" style={{ textAlign: 'center' }}>
              <h1 className="login-title">Password updated</h1>
              <p style={{
                fontSize: '13px',
                color: 'var(--kahla-text-light)',
                lineHeight: '1.7',
                letterSpacing: '0.02em',
                marginBottom: '24px',
              }}>
                Your password has been changed successfully.<br />
                You can now sign in with your new credentials.
              </p>
              <button
                data-testid="reset-password-to-login-btn"
                className="btn-primary"
                onClick={() => navigate('/login')}
                style={{ padding: '12px 32px' }}
              >
                Sign In
              </button>
            </div>
          )}

          {!success && (
            <div style={{ marginTop: '32px', textAlign: 'center' }}>
              <button
                data-testid="reset-password-back-btn"
                className="btn-secondary"
                onClick={() => navigate('/login')}
                style={{ fontSize: '12px', padding: '10px 20px' }}
              >
                ← Back to Sign In
              </button>
            </div>
          )}
        </div>
      </div>

      <footer style={{
        width: '100vw',
        padding: '24px',
        textAlign: 'center',
        borderTop: '1px solid var(--kahla-border)',
        marginTop: 'auto',
      }}>
        <PlatformFooter variant="compact" />
      </footer>
    </div>
  );
};

export default ResetPassword;
